import React from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  IconButton,
  InputLabel,
  ListItemText,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import { Add, Delete, Download, Edit, Refresh, Visibility } from "@mui/icons-material";
import { callBackendApi } from "../../methods/axiosInstances/backendInstance/apiRequest";
import { showSnackbar } from "../../store/snackbarSlice/snackbarSlice";
import { useDispatch } from "react-redux";

const IGNORED_FIELDS = ["id", "created_at", "updated_at", "deleted_at"];

const normalizeRows = (data) => {
  if (Array.isArray(data)) {
    return data.filter((item) => item && typeof item === "object");
  }
  if (data && Array.isArray(data.data)) {
    return data.data.filter((item) => item && typeof item === "object");
  }
  if (data && typeof data === "object") return [data].filter((item) => item && typeof item === "object");
  return [];
};

const resolveRowIdentifier = (row, idField) => {
  if (!row || typeof row !== "object") return undefined;
  if (idField && row[idField] !== undefined && row[idField] !== null) return row[idField];
  if (row.id !== undefined && row.id !== null) return row.id;
  if (row.uuid !== undefined && row.uuid !== null) return row.uuid;
  return undefined;
};

const inferType = (value) => {
  if (typeof value === "boolean") return "boolean";
  if (typeof value === "number") return "number";
  if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value)) return "date";
  if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}[ T]\d{2}:\d{2}/.test(value)) return "datetime";
  return "text";
};

const normalizeDateTimeForInput = (value) => {
  if (value === null || value === undefined || value === "") return "";
  const text = String(value).trim();
  const match = text.match(/^(\d{4}-\d{2}-\d{2})[ T](\d{2}:\d{2})/);
  if (match) return `${match[1]}T${match[2]}`;
  return text;
};

const normalizeDateForInput = (value) => {
  if (value === null || value === undefined || value === "") return "";
  const text = String(value).trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(text)) return text;
  const match = text.match(/^(\d{4}-\d{2}-\d{2})[ T]/);
  if (match) return match[1];
  return text;
};

const castValue = (value, type) => {
  if (value === "") return null;
  if (type === "number") {
    const parsed = Number(value);
    return Number.isNaN(parsed) ? null : parsed;
  }
  if (type === "boolean") return Boolean(value);
  if (type === "datetime") {
    const text = String(value).trim();
    if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(text)) return `${text.replace("T", " ")}:00`;
    if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}$/.test(text)) return text.replace("T", " ");
    if (/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}$/.test(text)) return `${text}:00`;
    return text;
  }
  return value;
};

const payloadHasBinary = (payload) => Object.values(payload || {}).some(
  (value) => value instanceof File || value instanceof Blob
);

const payloadToFormData = (payload) => {
  const formData = new FormData();

  Object.entries(payload || {}).forEach(([key, value]) => {
    if (value === undefined || value === null) return;

    if (value instanceof File || value instanceof Blob) {
      formData.append(key, value);
      return;
    }

    if (typeof value === "boolean") {
      formData.append(key, value ? "1" : "0");
      return;
    }

    if (typeof value === "object") {
      formData.append(key, JSON.stringify(value));
      return;
    }

    formData.append(key, String(value));
  });

  return formData;
};

const normalizeSelectOptions = (rows, optionSource) => {
  const valueField = optionSource?.valueField || "id";
  const labelFields = Array.isArray(optionSource?.labelFields) && optionSource.labelFields.length
    ? optionSource.labelFields
    : ["name", "code", "uuid"];

  return rows.map((row) => {
    const label = labelFields
      .map((field) => row?.[field])
      .filter((value) => value !== null && value !== undefined && String(value).trim() !== "")
      .join(" - ");

    return {
      value: row?.[valueField],
      label: label || "Record",
    };
  }).filter((option) => option.value !== undefined && option.value !== null);
};

const normalizeValidationErrors = (validationErrors) => {
  if (!validationErrors || typeof validationErrors !== "object" || Array.isArray(validationErrors)) {
    return {};
  }

  return Object.fromEntries(
    Object.entries(validationErrors).map(([field, messages]) => {
      if (Array.isArray(messages)) {
        return [field, messages[0] || "Invalid value."];
      }
      return [field, String(messages)];
    })
  );
};

const resolveErrorMessage = (response, defaultMessage) => {
  if (typeof response?.error === "string" && response.error.trim() !== "") {
    return response.error;
  }

  if (typeof response?.errorData?.message === "string" && response.errorData.message.trim() !== "") {
    return response.errorData.message;
  }

  return defaultMessage;
};

const resolveEndpointTemplate = (template, value) => {
  if (!template || value === null || value === undefined || value === "") return "";
  return String(template).replace("{id}", String(value));
};

const stripEndpointQuery = (endpoint) => {
  if (!endpoint) return "";
  return String(endpoint).split("?")[0];
};

const buildItemEndpoint = (endpoint, id) => {
  const base = stripEndpointQuery(endpoint);
  if (id === undefined || id === null || id === "") return base;
  return `${base}/${id}`;
};

const toPrettyJson = (value) => {
  if (value === null || value === undefined || value === "") return "";

  if (typeof value === "object") {
    try {
      return JSON.stringify(value, null, 2);
    } catch (_) {
      return String(value);
    }
  }

  if (typeof value === "string") {
    const trimmed = value.trim();
    if (trimmed === "") return "";
    try {
      return JSON.stringify(JSON.parse(trimmed), null, 2);
    } catch (_) {
      return value;
    }
  }

  return String(value);
};

const parseJsonObject = (value) => {
  if (value === null || value === undefined || value === "") return null;
  if (typeof value === "object") return value;
  if (typeof value === "string") {
    try {
      return JSON.parse(value);
    } catch (_) {
      return null;
    }
  }
  return null;
};

const hasCustomFormRenderer = (resourceDef) => typeof resourceDef?.renderForm === "function";

const deriveFields = (resourceDef, rows) => {
  if (Array.isArray(resourceDef?.formFields) && resourceDef.formFields.length > 0) {
    return resourceDef.formFields;
  }

  const sample = rows.find((row) => row && typeof row === "object");
  if (!sample) return [];

  return Object.keys(sample)
    .filter((key) => !IGNORED_FIELDS.includes(key))
    .map((key) => ({
      name: key,
      label: key,
      type: inferType(sample[key]),
    }));
};

const resolveSelectSourceFields = (resourceDef, fallbackFields = []) => {
  if (Array.isArray(resourceDef?.selectSources) && resourceDef.selectSources.length > 0) {
    return resourceDef.selectSources;
  }

  return fallbackFields;
};

const buildInitialFormData = (resourceDef, fields, row = null) => {
  if (row && typeof resourceDef?.mapRowToFormData === "function") {
    const mapped = resourceDef.mapRowToFormData(row);
    if (mapped && typeof mapped === "object") {
      return { ...mapped };
    }
  }

  if (!row && typeof resourceDef?.createInitialFormData === "function") {
    const initial = resourceDef.createInitialFormData();
    if (initial && typeof initial === "object") {
      return { ...initial };
    }
  }

  if (Array.isArray(fields) && fields.length > 0) {
    const next = {};

    fields.forEach((field) => {
      if (row) {
        const existing = row?.[field.name];
        const fallbackValue = field.type === "boolean"
          ? false
          : field.type === "select" && field.multiple
            ? []
            : "";

        next[field.name] = existing === null || existing === undefined
          ? fallbackValue
          : existing;
        return;
      }

      const fallbackValue = field.type === "boolean"
        ? false
        : field.type === "select" && field.multiple
          ? []
          : "";

      next[field.name] = field.defaultValue ?? fallbackValue;
    });

    return next;
  }

  if (row && typeof row === "object") {
    return { ...row };
  }

  return {};
};

const buildPayload = (resourceDef, fields, formData) => {
  if (typeof resourceDef?.toPayload === "function") {
    const payload = resourceDef.toPayload(formData || {});
    if (payload && typeof payload === "object") {
      return payload;
    }
    return {};
  }

  if (!Array.isArray(fields) || fields.length === 0) {
    return formData && typeof formData === "object" ? { ...formData } : {};
  }

  const payload = {};
  fields.forEach((field) => {
    payload[field.name] = castValue(formData?.[field.name], field.type);
  });

  return payload;
};

const buildColumns = (rows, explicitFields = []) => {
  const hasExplicitFields = Array.isArray(explicitFields) && explicitFields.length > 0;
  const keys = new Set();
  const explicitFieldByName = Object.fromEntries(
    (explicitFields || [])
      .filter((field) => field?.name)
      .map((field) => [field.name, field])
  );

  explicitFields.forEach((field) => {
    if (field?.name) keys.add(field.name);
  });

  if (!hasExplicitFields) {
    rows.slice(0, 20).forEach((row) => {
      if (!row || typeof row !== "object") return;
      Object.keys(row).forEach((key) => {
        if (key !== "id") {
          keys.add(key);
        }
      });
    });
  }

  const labels = Object.fromEntries(
    explicitFields
      .filter((field) => field?.name)
      .map((field) => [field.name, field.label || field.name])
  );

  return Array.from(keys).slice(0, 10).map((fieldName) => {
    const explicitField = explicitFieldByName[fieldName] || {};

    return {
      field: fieldName,
      headerName: labels[fieldName] || fieldName,
      flex: fieldName === "id" ? undefined : 1,
      width: fieldName === "id" ? 90 : undefined,
      valueGetter: (...args) => {
        const row = args?.[1] ?? args?.[0]?.row;

        if (typeof explicitField.valueGetter === "function") {
          const customValue = explicitField.valueGetter(row, args);
          if (customValue !== null && customValue !== undefined) {
            return customValue;
          }
        }

        const value = row?.[fieldName];
        if (value === null || value === undefined) return "";
        if (typeof value === "object") return JSON.stringify(value);
        return value;
      },
    };
  });
};

function FormFields({ fields, formData, setFormData, selectOptionsMap, errors, clearFieldError, onPreviewDocument }) {
  return (
    <Box sx={{ mt: 1, display: "grid", gap: 2, gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" } }}>
      {fields.map((field) => {
        const rawValue = formData[field.name];
        const value = field.type === "datetime"
          ? normalizeDateTimeForInput(rawValue)
          : field.type === "date"
            ? normalizeDateForInput(rawValue)
            : rawValue;
        const errorMessage = errors?.[field.name] || "";

        if (field.type === "file") {
          const selectedFileName = value instanceof File ? value.name : "";

          return (
            <TextField
              key={field.name}
              fullWidth
              type="file"
              label={field.label}
              InputLabelProps={{ shrink: true }}
              inputProps={{ accept: field.accept || "*" }}
              disabled={Boolean(field.disabled)}
              onChange={(event) => {
                const file = event.target.files?.[0] || null;
                setFormData((prev) => ({ ...prev, [field.name]: file }));
                clearFieldError?.(field.name);
              }}
              error={Boolean(errorMessage)}
              helperText={errorMessage || selectedFileName || " "}
            />
          );
        }

        if (field.type === "boolean") {
          return (
            <FormControl key={field.name} error={Boolean(errorMessage)}>
              <FormControlLabel
                control={(
                  <Checkbox
                    checked={Boolean(value)}
                    disabled={Boolean(field.disabled)}
                    onChange={(event) => {
                      setFormData((prev) => ({ ...prev, [field.name]: event.target.checked }));
                      clearFieldError?.(field.name);
                    }}
                  />
                )}
                label={field.label}
              />
              {errorMessage ? <FormHelperText>{errorMessage}</FormHelperText> : null}
            </FormControl>
          );
        }

        if (field.type === "select") {
          const options = Array.isArray(field.options)
            ? field.options
            : (selectOptionsMap?.[field.name] || []);
          const isMultiple = Boolean(field.multiple);
          const normalizedValue = isMultiple
            ? (Array.isArray(value) ? value : (value ? [value] : []))
            : (value ?? "");
          const canPreview = Boolean(field.previewDocument?.endpointTemplate && value);
          const previewEndpoint = resolveEndpointTemplate(field.previewDocument?.endpointTemplate, value);
          const previewMode = field.previewDocument?.mode || "inline";
          const previewLabel = field.previewDocument?.label || "Preview";

          return (
            <Box key={field.name} sx={{ display: "flex", gap: 1, alignItems: "flex-start" }}>
              <FormControl fullWidth error={Boolean(errorMessage)}>
                <InputLabel>{field.label}</InputLabel>
                <Select
                  multiple={isMultiple}
                  value={normalizedValue}
                  label={field.label}
                  disabled={Boolean(field.disabled)}
                  onChange={(event) => {
                    const nextValue = isMultiple
                      ? (typeof event.target.value === "string"
                        ? event.target.value.split(",").map((item) => item.trim()).filter(Boolean)
                        : event.target.value)
                      : event.target.value;

                    setFormData((prev) => ({ ...prev, [field.name]: nextValue }));
                    clearFieldError?.(field.name);
                  }}
                  renderValue={isMultiple
                    ? (selected) => {
                      const selectedValues = Array.isArray(selected) ? selected : [];
                      if (!selectedValues.length) {
                        return "";
                      }

                      return selectedValues
                        .map((selectedValue) => {
                          const selectedOption = options.find((option) => String(option.value) === String(selectedValue));
                          return selectedOption?.label || String(selectedValue);
                        })
                        .join(", ");
                    }
                    : undefined}
                >
                  {field.nullable && !isMultiple ? <MenuItem value="">None</MenuItem> : null}
                  {options.map((option) => (
                    <MenuItem key={`${field.name}-${option.value}`} value={option.value}>
                      {isMultiple ? (
                        <>
                          <Checkbox
                            checked={normalizedValue.some(
                              (selectedValue) => String(selectedValue) === String(option.value)
                            )}
                          />
                          <ListItemText primary={option.label} />
                        </>
                      ) : (
                        option.label
                      )}
                    </MenuItem>
                  ))}
                </Select>
                {errorMessage ? <FormHelperText>{errorMessage}</FormHelperText> : null}
              </FormControl>
              {canPreview ? (
                <Button
                  variant="outlined"
                  size="small"
                  sx={{ mt: 0.5, whiteSpace: "nowrap" }}
                  onClick={() => onPreviewDocument?.(previewEndpoint, "document", previewMode)}
                >
                  {previewLabel}
                </Button>
              ) : null}
            </Box>
          );
        }

        if (field.type === "radio") {
          const options = Array.isArray(field.options) ? field.options : [];
          const normalizedValue = value === true || value === false ? String(value) : (value ?? "");

          return (
            <FormControl key={field.name} error={Boolean(errorMessage)}>
              <FormLabel>{field.label}</FormLabel>
              <RadioGroup
                row
                value={normalizedValue}
                onChange={(event) => {
                  setFormData((prev) => ({ ...prev, [field.name]: event.target.value }));
                  clearFieldError?.(field.name);
                }}
              >
                {options.map((option) => (
                  <FormControlLabel
                    key={`${field.name}-${String(option.value)}`}
                    value={String(option.value)}
                    control={<Radio />}
                    label={option.label}
                    disabled={Boolean(field.disabled)}
                  />
                ))}
              </RadioGroup>
              {errorMessage ? <FormHelperText>{errorMessage}</FormHelperText> : null}
            </FormControl>
          );
        }

        if (field.renderAsTable) {
          const parsed = parseJsonObject(value);
          const entries = parsed && typeof parsed === "object"
            ? Object.entries(parsed)
            : [];

          return (
            <Box key={field.name} sx={{ gridColumn: "1 / -1" }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>{field.label}</Typography>
              {entries.length > 0 ? (
                <Table size="small">
                  <TableBody>
                    {entries.map(([k, v]) => (
                      <TableRow key={`${field.name}-${k}`}>
                        <TableCell sx={{ width: 240, fontWeight: 600 }}>{k}</TableCell>
                        <TableCell>
                          {typeof v === "object" ? JSON.stringify(v, null, 2) : String(v)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <Typography variant="body2" color="text.secondary">No structured data available.</Typography>
              )}
            </Box>
          );
        }

        return (
          <Box key={field.name} sx={{ display: "flex", gap: 1, alignItems: "flex-start" }}>
            <TextField
              fullWidth
              label={field.label}
              value={field.formatJson ? toPrettyJson(value) : (value ?? "")}
              type={field.type === "datetime" ? "datetime-local" : field.type}
              disabled={Boolean(field.disabled)}
              InputLabelProps={
                field.type === "date" || field.type === "datetime"
                  ? { shrink: true }
                  : undefined
              }
              multiline={Boolean(field.formatJson || field.multiline)}
              minRows={field.formatJson ? 8 : undefined}
              InputProps={field.readOnly ? { readOnly: true } : undefined}
              onChange={(event) => {
                setFormData((prev) => ({ ...prev, [field.name]: event.target.value }));
                clearFieldError?.(field.name);
              }}
              placeholder={field.placeholder}
              error={Boolean(errorMessage)}
              helperText={errorMessage || " "}
            />
            {field.previewDocument?.endpointTemplate && value ? (
              <Button
                variant="outlined"
                size="small"
                sx={{ mt: 0.5, whiteSpace: "nowrap" }}
                onClick={() =>
                  onPreviewDocument?.(
                    resolveEndpointTemplate(field.previewDocument.endpointTemplate, value),
                    "document",
                    field.previewDocument?.mode || "inline"
                  )
                }
              >
                {field.previewDocument?.label || "Preview"}
              </Button>
            ) : null}
          </Box>
        );
      })}
    </Box>
  );
}

export default function ResourceCrudPanel({ resource, resourceLookup = {} }) {
  const dispatch = useDispatch();

  const [rows, setRows] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [errorMessage, setErrorMessage] = React.useState("");

  const [open, setOpen] = React.useState(false);
  const [isEditing, setIsEditing] = React.useState(false);
  const [selectedRow, setSelectedRow] = React.useState(null);
  const [formData, setFormData] = React.useState({});
  const [formErrors, setFormErrors] = React.useState({});
  const [formGeneralError, setFormGeneralError] = React.useState("");

  const [relatedTab, setRelatedTab] = React.useState(0);
  const [relatedLoading, setRelatedLoading] = React.useState(false);
  const [relatedError, setRelatedError] = React.useState("");
  const [relatedData, setRelatedData] = React.useState({});

  const [relatedModalOpen, setRelatedModalOpen] = React.useState(false);
  const [relatedModalEditing, setRelatedModalEditing] = React.useState(false);
  const [relatedModalRow, setRelatedModalRow] = React.useState(null);
  const [relatedModalFormData, setRelatedModalFormData] = React.useState({});
  const [relatedFormErrors, setRelatedFormErrors] = React.useState({});
  const [relatedFormGeneralError, setRelatedFormGeneralError] = React.useState("");
  const [mainSelectOptions, setMainSelectOptions] = React.useState({});
  const [relatedSelectOptions, setRelatedSelectOptions] = React.useState({});

  const usesCustomForm = React.useMemo(() => hasCustomFormRenderer(resource), [resource]);
  const MainCustomForm = usesCustomForm ? resource?.renderForm : null;

  const clearMainFieldError = React.useCallback((fieldName) => {
    setFormGeneralError("");
    setFormErrors((prev) => {
      if (!prev[fieldName]) return prev;
      const next = { ...prev };
      delete next[fieldName];
      return next;
    });
  }, []);

  const clearRelatedFieldError = React.useCallback((fieldName) => {
    setRelatedFormGeneralError("");
    setRelatedFormErrors((prev) => {
      if (!prev[fieldName]) return prev;
      const next = { ...prev };
      delete next[fieldName];
      return next;
    });
  }, []);

  const openBinaryDocument = React.useCallback(async (endpoint, fileName, mode = "download") => {
    const response = await callBackendApi(endpoint, "GET", null, false, { responseType: "blob" });
    if (!response?.success) {
      dispatch(showSnackbar({ message: response?.error || "Failed to fetch document." }));
      return;
    }

    const blob = response.data instanceof Blob ? response.data : new Blob([response.data]);
    const blobUrl = window.URL.createObjectURL(blob);

    if (mode === "inline") {
      window.open(blobUrl, "_blank", "noopener,noreferrer");
      setTimeout(() => window.URL.revokeObjectURL(blobUrl), 60000);
      return;
    }

    const link = document.createElement("a");
    link.href = blobUrl;
    link.download = fileName || "document";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setTimeout(() => window.URL.revokeObjectURL(blobUrl), 60000);
  }, [dispatch]);

  const resolveActionIcon = React.useCallback((icon) => {
    if (React.isValidElement(icon)) return icon;
    if (icon === "view") return <Visibility fontSize="small" />;
    if (icon === "download") return <Download fontSize="small" />;
    if (icon === "edit") return <Edit fontSize="small" />;
    if (icon === "delete") return <Delete fontSize="small" />;
    return <Edit fontSize="small" />;
  }, []);

  const loadRows = React.useCallback(async () => {
    if (!resource?.endpoint) return;

    setLoading(true);
    setErrorMessage("");

    const response = await callBackendApi(resource.endpoint, "GET");

    if (response?.success) {
      setRows(normalizeRows(response.data));
    } else {
      setRows([]);
      setErrorMessage("Failed to load data for this resource.");
    }

    setLoading(false);
  }, [resource]);

  React.useEffect(() => {
    loadRows();
  }, [loadRows]);

  const derivedFields = React.useMemo(
    () => (usesCustomForm ? [] : deriveFields(resource, rows)),
    [resource, rows, usesCustomForm]
  );

  const tableFields = React.useMemo(() => {
    if (Array.isArray(resource?.tableFields) && resource.tableFields.length > 0) {
      return resource.tableFields;
    }
    return derivedFields;
  }, [resource, derivedFields]);

  const mainSelectSourceFields = React.useMemo(
    () => resolveSelectSourceFields(resource, derivedFields),
    [resource, derivedFields]
  );

  const relatedTabs = React.useMemo(
    () => (Array.isArray(resource?.relatedTabs) ? resource.relatedTabs : []),
    [resource]
  );

  const currentRelatedTab = relatedTabs[relatedTab];
  const currentRelatedResource = currentRelatedTab ? resourceLookup[currentRelatedTab.key] : null;
  const currentRelatedResourceDef = currentRelatedResource || currentRelatedTab || null;
  const currentRelatedReadOnly = Boolean(currentRelatedTab?.readOnly || currentRelatedResourceDef?.readOnly);
  const currentRelatedRows = currentRelatedTab ? relatedData[currentRelatedTab.key] || [] : [];
  const currentRelatedUsesCustomForm = hasCustomFormRenderer(currentRelatedResourceDef);
  const RelatedCustomForm = currentRelatedUsesCustomForm ? currentRelatedResourceDef?.renderForm : null;

  const currentRelatedFields = React.useMemo(() => {
    if (currentRelatedUsesCustomForm) {
      return [];
    }

    return deriveFields(currentRelatedResourceDef, currentRelatedRows);
  }, [currentRelatedUsesCustomForm, currentRelatedResourceDef, currentRelatedRows]);

  const currentRelatedTableFields = React.useMemo(() => {
    if (Array.isArray(currentRelatedResourceDef?.tableFields) && currentRelatedResourceDef.tableFields.length > 0) {
      return currentRelatedResourceDef.tableFields;
    }
    return currentRelatedFields;
  }, [currentRelatedResourceDef, currentRelatedFields]);

  const currentRelatedSelectSourceFields = React.useMemo(
    () => resolveSelectSourceFields(currentRelatedResourceDef, currentRelatedFields),
    [currentRelatedResourceDef, currentRelatedFields]
  );

  const tableColumns = React.useMemo(() => {
    return [
      ...buildColumns(rows, tableFields),
      {
        field: "actions",
        headerName: "Actions",
        width: Number(resource?.actionsColumnWidth) > 0 ? Number(resource.actionsColumnWidth) : 120,
        sortable: false,
        filterable: false,
        renderCell: (params) => {
          const customActions = typeof resource?.rowActions === "function"
            ? resource.rowActions({
              row: params.row,
              reload: loadRows,
              openBinaryDocument,
            }) || []
            : [];

          return (
            <Stack direction="row" spacing={0.5}>
              {customActions.map((action, idx) => (
                <Tooltip key={`main-action-${idx}`} title={action?.label || ""}>
                  <IconButton
                    size="small"
                    color={action?.color || "default"}
                    onClick={() => action?.onClick?.()}
                  >
                    {resolveActionIcon(action?.icon)}
                  </IconButton>
                </Tooltip>
              ))}
              <IconButton size="small" color="primary" onClick={() => openEditDialog(params.row)}>
                <Edit fontSize="small" />
              </IconButton>
              <IconButton
                size="small"
                color="error"
                onClick={() => handleDelete(resolveRowIdentifier(params.row, resource?.idField))}
              >
                <Delete fontSize="small" />
              </IconButton>
            </Stack>
          );
        },
      },
    ];
  }, [rows, tableFields, resource, loadRows, openBinaryDocument, resolveActionIcon]);

  const closeDialog = () => {
    setOpen(false);
    setIsEditing(false);
    setSelectedRow(null);
    setFormData({});
    setFormErrors({});
    setFormGeneralError("");
    setRelatedData({});
    setRelatedError("");
    setRelatedTab(0);
    setRelatedFormErrors({});
    setRelatedFormGeneralError("");
    setMainSelectOptions({});
    setRelatedSelectOptions({});
  };

  const loadSelectOptionsForFields = React.useCallback(async (fields) => {
    const fieldsWithSource = (fields || []).filter((field) => field?.optionSource?.endpoint);
    if (!fieldsWithSource.length) {
      return {};
    }

    const responses = await Promise.all(
      fieldsWithSource.map(async (field) => {
        const response = await callBackendApi(field.optionSource.endpoint, "GET");
        if (!response?.success) {
          return { fieldName: field.name, options: [] };
        }

        const optionRows = normalizeRows(response.data);
        return {
          fieldName: field.name,
          options: normalizeSelectOptions(optionRows, field.optionSource),
        };
      })
    );

    return Object.fromEntries(responses.map((entry) => [entry.fieldName, entry.options]));
  }, []);

  const openCreateDialog = () => {
    const initialData = buildInitialFormData(resource, derivedFields);

    setFormData(initialData);
    setIsEditing(false);
    setSelectedRow(null);
    setFormErrors({});
    setFormGeneralError("");
    setOpen(true);

    loadSelectOptionsForFields(mainSelectSourceFields).then((optionsMap) => setMainSelectOptions(optionsMap));
  };

  const loadRelatedData = React.useCallback(
    async (row) => {
      if (!relatedTabs.length) {
        setRelatedData({});
        return;
      }

      const sourceField = resource?.idField || "id";
      const sourceValue = row?.[sourceField];
      if (sourceValue === undefined || sourceValue === null) {
        setRelatedData({});
        return;
      }

      setRelatedLoading(true);
      setRelatedError("");

      const fetchedByKey = {};
      const fetchResults = await Promise.all(
        relatedTabs.map(async (tab) => {
          const response = await callBackendApi(tab.endpoint, "GET");
          return { tab, response };
        })
      );

      let hasError = false;
      fetchResults.forEach(({ tab, response }) => {
        if (!response?.success) {
          hasError = true;
          fetchedByKey[tab.key] = [];
          return;
        }
        fetchedByKey[tab.key] = normalizeRows(response.data);
      });

      const computed = {};
      let passes = 0;
      const maxPasses = relatedTabs.length + 2;

      while (passes < maxPasses) {
        let changed = false;

        relatedTabs.forEach((tab) => {
          if (computed[tab.key]) return;

          if (tab.foreignKey) {
            computed[tab.key] = fetchedByKey[tab.key].filter(
              (item) => String(item?.[tab.foreignKey]) === String(sourceValue)
            );
            changed = true;
            return;
          }

          if (tab.fromTab && tab.fromField && tab.toField && computed[tab.fromTab]) {
            const sourceIds = new Set(
              computed[tab.fromTab]
                .map((item) => item?.[tab.fromField])
                .filter((value) => value !== null && value !== undefined)
            );

            computed[tab.key] = fetchedByKey[tab.key].filter((item) => sourceIds.has(item?.[tab.toField]));
            changed = true;
          }
        });

        if (!changed) break;
        passes += 1;
      }

      relatedTabs.forEach((tab) => {
        if (!computed[tab.key]) {
          computed[tab.key] = [];
        }
      });

      setRelatedData(computed);
      setRelatedTab((previousTabIndex) => {
        const previousTab = relatedTabs[previousTabIndex];
        if (previousTab && (computed[previousTab.key] || []).length > 0) {
          return previousTabIndex;
        }

        const firstTabWithDataIndex = relatedTabs.findIndex((tab) => (computed[tab.key] || []).length > 0);
        return firstTabWithDataIndex >= 0 ? firstTabWithDataIndex : 0;
      });
      if (hasError) {
        setRelatedError("Some related tabs could not be loaded.");
      }
      setRelatedLoading(false);
    },
    [relatedTabs, resource]
  );

  const openEditDialog = (row) => {
    const initialData = buildInitialFormData(resource, derivedFields, row);

    setFormData(initialData);
    setIsEditing(true);
    setSelectedRow(row);
    setFormErrors({});
    setFormGeneralError("");
    setRelatedTab(0);
    loadRelatedData(row);
    setOpen(true);

    loadSelectOptionsForFields(mainSelectSourceFields).then((optionsMap) => setMainSelectOptions(optionsMap));
  };

  const handleSave = async () => {
    const payload = buildPayload(resource, derivedFields, formData);
    const requestData = payloadHasBinary(payload) ? payloadToFormData(payload) : payload;

    const rowIdentifier = resolveRowIdentifier(selectedRow, resource?.idField);
    if (isEditing && (rowIdentifier === undefined || rowIdentifier === null)) {
      dispatch(showSnackbar({ message: "Cannot resolve row identifier." }));
      return;
    }

    const endpoint = isEditing ? buildItemEndpoint(resource.endpoint, rowIdentifier) : resource.endpoint;
    const method = isEditing ? "PUT" : "POST";
    const response = await callBackendApi(endpoint, method, requestData);

    if (response?.success) {
      setFormErrors({});
      setFormGeneralError("");

      // Create flow: keep dialog open and switch to edit mode so related data can be managed immediately.
      if (!isEditing && relatedTabs.length > 0) {
        const savedRows = normalizeRows(response.data);
        const savedRow = savedRows[0] || null;

        if (savedRow) {
          setIsEditing(true);
          setSelectedRow(savedRow);
          setFormData(buildInitialFormData(resource, derivedFields, savedRow));
          setRelatedData({});
          setRelatedError("");
          setRelatedTab(0);
          await loadRelatedData(savedRow);
          dispatch(showSnackbar({ message: `${resource.label} saved. You can now add related information.` }));
          loadRows();
          return;
        }
      }

      dispatch(showSnackbar({ message: `${resource.label} saved.` }));
      closeDialog();
      loadRows();
      return;
    }

    const validationErrors = normalizeValidationErrors(response?.validationErrors);
    const hasValidationErrors = Object.keys(validationErrors).length > 0;
    const message = resolveErrorMessage(
      response,
      hasValidationErrors ? "Please fix the highlighted validation errors." : "Validation or save error."
    );

    setFormErrors(validationErrors);
    setFormGeneralError(message);
    dispatch(showSnackbar({ message }));
  };

  const handleDelete = async (id) => {
    if (id === undefined || id === null) {
      dispatch(showSnackbar({ message: "Cannot resolve row identifier." }));
      return;
    }

    const confirmed = window.confirm(`Delete ${resource.label} item #${id}? This cannot be undone.`);
    if (!confirmed) return;

    const response = await callBackendApi(buildItemEndpoint(resource.endpoint, id), "DELETE");

    if (response?.success) {
      dispatch(showSnackbar({ message: `${resource.label} deleted.` }));
      loadRows();
      return;
    }

    dispatch(showSnackbar({ message: "Delete failed." }));
  };

  const openRelatedCreateModal = () => {
    if (!currentRelatedTab || !selectedRow || currentRelatedReadOnly) return;

    const initial = buildInitialFormData(currentRelatedResourceDef, currentRelatedFields);

    if (currentRelatedTab.foreignKey) {
      const parentId = selectedRow[resource?.idField || "id"];
      initial[currentRelatedTab.foreignKey] = parentId;
    }

    setRelatedModalEditing(false);
    setRelatedModalRow(null);
    setRelatedModalFormData(initial);
    setRelatedFormErrors({});
    setRelatedFormGeneralError("");
    setRelatedModalOpen(true);

    loadSelectOptionsForFields(currentRelatedSelectSourceFields).then((optionsMap) => setRelatedSelectOptions(optionsMap));
  };

  const openRelatedEditModal = (row) => {
    if (currentRelatedReadOnly) return;

    const initial = buildInitialFormData(currentRelatedResourceDef, currentRelatedFields, row);

    setRelatedModalEditing(true);
    setRelatedModalRow(row);
    setRelatedModalFormData(initial);
    setRelatedFormErrors({});
    setRelatedFormGeneralError("");
    setRelatedModalOpen(true);

    loadSelectOptionsForFields(currentRelatedSelectSourceFields).then((optionsMap) => setRelatedSelectOptions(optionsMap));
  };

  const closeRelatedModal = () => {
    setRelatedModalOpen(false);
    setRelatedModalEditing(false);
    setRelatedModalRow(null);
    setRelatedModalFormData({});
    setRelatedFormErrors({});
    setRelatedFormGeneralError("");
    setRelatedSelectOptions({});
  };

  const saveRelatedModal = async () => {
    if (!currentRelatedTab || currentRelatedReadOnly) return;

    const payload = buildPayload(currentRelatedResourceDef, currentRelatedFields, relatedModalFormData);

    if (currentRelatedTab.foreignKey && selectedRow) {
      payload[currentRelatedTab.foreignKey] = selectedRow[resource?.idField || "id"];
    }

    const relatedIdField = currentRelatedResourceDef?.idField;
    const rowIdentifier = resolveRowIdentifier(relatedModalRow, relatedIdField);

    if (relatedModalEditing && (rowIdentifier === undefined || rowIdentifier === null)) {
      dispatch(showSnackbar({ message: "Cannot resolve related row identifier." }));
      return;
    }

    const endpoint = relatedModalEditing
      ? buildItemEndpoint(currentRelatedTab.endpoint, rowIdentifier)
      : currentRelatedTab.endpoint;
    const method = relatedModalEditing ? "PUT" : "POST";
    const requestData = payloadHasBinary(payload) ? payloadToFormData(payload) : payload;

    const response = await callBackendApi(endpoint, method, requestData);
    if (response?.success) {
      setRelatedFormErrors({});
      setRelatedFormGeneralError("");
      dispatch(showSnackbar({ message: `${currentRelatedTab.label} saved.` }));
      closeRelatedModal();
      loadRelatedData(selectedRow);
      return;
    }

    const validationErrors = normalizeValidationErrors(response?.validationErrors);
    const hasValidationErrors = Object.keys(validationErrors).length > 0;
    const message = resolveErrorMessage(
      response,
      hasValidationErrors ? "Please fix the highlighted validation errors." : "Failed to save related data."
    );

    setRelatedFormErrors(validationErrors);
    setRelatedFormGeneralError(message);
    dispatch(showSnackbar({ message }));
  };

  const deleteRelatedRow = async (row) => {
    if (!currentRelatedTab || currentRelatedReadOnly) return;

    const relatedIdField = currentRelatedResourceDef?.idField;
    const rowIdentifier = resolveRowIdentifier(row, relatedIdField);
    if (rowIdentifier === undefined || rowIdentifier === null) {
      dispatch(showSnackbar({ message: "Cannot resolve related row identifier." }));
      return;
    }

    const confirmed = window.confirm(`Delete ${currentRelatedTab.label} item #${rowIdentifier}?`);
    if (!confirmed) return;

    const response = await callBackendApi(buildItemEndpoint(currentRelatedTab.endpoint, rowIdentifier), "DELETE");

    if (response?.success) {
      dispatch(showSnackbar({ message: `${currentRelatedTab.label} deleted.` }));
      loadRelatedData(selectedRow);
      return;
    }

    dispatch(showSnackbar({ message: "Failed to delete related data." }));
  };

  const relatedColumns = React.useMemo(() => {
    if (!currentRelatedTab) return [];

    return [
      ...buildColumns(currentRelatedRows, currentRelatedTableFields),
      {
        field: "actions",
        headerName: "Actions",
        width: Number(currentRelatedResourceDef?.actionsColumnWidth) > 0
          ? Number(currentRelatedResourceDef.actionsColumnWidth)
          : 120,
        sortable: false,
        filterable: false,
        renderCell: (params) => {
          const customActions = typeof currentRelatedResourceDef?.rowActions === "function"
            ? currentRelatedResourceDef.rowActions({
              row: params.row,
              reload: () => loadRelatedData(selectedRow),
              openBinaryDocument,
            }) || []
            : [];

          return (
            <Stack direction="row" spacing={0.5}>
              {customActions.map((action, idx) => (
                <Tooltip key={`related-action-${idx}`} title={action?.label || ""}>
                  <IconButton
                    size="small"
                    color={action?.color || "default"}
                    onClick={() => action?.onClick?.()}
                  >
                    {resolveActionIcon(action?.icon)}
                  </IconButton>
                </Tooltip>
              ))}
              {!currentRelatedReadOnly ? (
                <>
                  <IconButton size="small" color="primary" onClick={() => openRelatedEditModal(params.row)}>
                    <Edit fontSize="small" />
                  </IconButton>
                  <IconButton size="small" color="error" onClick={() => deleteRelatedRow(params.row)}>
                    <Delete fontSize="small" />
                  </IconButton>
                </>
              ) : null}
            </Stack>
          );
        },
      },
    ];
  }, [
    currentRelatedRows,
    currentRelatedTableFields,
    currentRelatedTab,
    currentRelatedResourceDef,
    selectedRow,
    loadRelatedData,
    openBinaryDocument,
    resolveActionIcon,
  ]);

  if (!resource) {
    return (
      <Card>
        <CardContent>
          <Typography color="error">No resource selected.</Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent>
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
          <Box>
            <Typography variant="h6">{resource.label}</Typography>
            <Typography variant="body2" color="text.secondary">
              {resource.description || "Manage records for this resource."}
            </Typography>
          </Box>
          <Stack direction="row" spacing={1}>
            <Button variant="outlined" startIcon={<Refresh />} onClick={loadRows}>Refresh</Button>
            <Button variant="contained" onClick={openCreateDialog}>New</Button>
          </Stack>
        </Stack>

        {errorMessage ? <Alert severity="error" sx={{ mb: 2 }}>{errorMessage}</Alert> : null}

        <Box sx={{ width: "100%" }}>
          <DataGrid
            rows={rows}
            columns={tableColumns}
            loading={loading}
            getRowId={(row, index) => resolveRowIdentifier(row, resource?.idField) ?? `${resource.key}-${index}`}
            pageSizeOptions={[10, 20, 50]}
            initialState={{ pagination: { paginationModel: { pageSize: 10, page: 0 } } }}
            autoHeight
            slots={{ toolbar: GridToolbar }}
          />
        </Box>
      </CardContent>

      <Dialog open={open} onClose={closeDialog} fullWidth maxWidth="lg">
        <DialogTitle>{isEditing ? `Edit ${resource.label}` : `Create ${resource.label}`}</DialogTitle>
        <DialogContent>
          {formGeneralError ? <Alert severity="error" sx={{ mt: 1 }}>{formGeneralError}</Alert> : null}

          {usesCustomForm && typeof MainCustomForm === "function"
            ? (
              <MainCustomForm
                formData={formData}
                setFormData={setFormData}
                errors={formErrors}
                clearFieldError={clearMainFieldError}
                selectOptions={mainSelectOptions}
                isEditing={isEditing}
                selectedRow={selectedRow}
                resource={resource}
              />
            )
            : (
              <FormFields
                fields={derivedFields}
                formData={formData}
                setFormData={setFormData}
                selectOptionsMap={mainSelectOptions}
                errors={formErrors}
                clearFieldError={clearMainFieldError}
                onPreviewDocument={openBinaryDocument}
              />
            )}

          {isEditing && relatedTabs.length > 0 ? (
            <Box sx={{ mt: 3 }}>
              <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                <Typography variant="subtitle2">Related Information</Typography>
                {!currentRelatedReadOnly ? (
                  <Button
                    variant="outlined"
                    startIcon={<Add />}
                    onClick={openRelatedCreateModal}
                    disabled={!currentRelatedTab}
                  >
                    New In Current Tab
                  </Button>
                ) : null}
              </Stack>

              <Tabs
                value={relatedTab}
                onChange={(_, value) => setRelatedTab(value)}
                variant="scrollable"
                scrollButtons="auto"
              >
                {relatedTabs.map((tab) => (
                  <Tab key={tab.key} label={tab.label} />
                ))}
              </Tabs>

              {relatedError ? <Alert severity="warning" sx={{ mt: 1 }}>{relatedError}</Alert> : null}

              {relatedLoading ? (
                <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
                  <CircularProgress size={24} />
                </Box>
              ) : (
                <Box sx={{ mt: 2 }}>
                  <DataGrid
                    rows={currentRelatedRows}
                    columns={relatedColumns}
                    getRowId={(row, index) =>
                      resolveRowIdentifier(row, currentRelatedResourceDef?.idField) ??
                      row?.id ??
                      row?.uuid ??
                      `${currentRelatedTab?.key}-${index}`
                    }
                    pageSizeOptions={[5, 10, 20]}
                    initialState={{ pagination: { paginationModel: { pageSize: 5, page: 0 } } }}
                    autoHeight
                    disableRowSelectionOnClick
                  />
                </Box>
              )}
            </Box>
          ) : (!isEditing && relatedTabs.length > 0 ? (
            <Alert severity="info" sx={{ mt: 3 }}>
              Save this record first to manage related information.
            </Alert>
          ) : null)}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog}>Cancel</Button>
          <Button variant="contained" onClick={handleSave}>Save</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={relatedModalOpen} onClose={closeRelatedModal} fullWidth maxWidth="md">
        <DialogTitle>
          {relatedModalEditing ? `Edit ${currentRelatedTab?.label}` : `Create ${currentRelatedTab?.label}`}
        </DialogTitle>
        <DialogContent>
          {relatedFormGeneralError ? <Alert severity="error" sx={{ mt: 1 }}>{relatedFormGeneralError}</Alert> : null}

          {currentRelatedUsesCustomForm && typeof RelatedCustomForm === "function"
            ? (
              <RelatedCustomForm
                formData={relatedModalFormData}
                setFormData={setRelatedModalFormData}
                errors={relatedFormErrors}
                clearFieldError={clearRelatedFieldError}
                selectOptions={relatedSelectOptions}
                isEditing={relatedModalEditing}
                selectedRow={relatedModalRow}
                resource={currentRelatedResourceDef}
                parentRow={selectedRow}
              />
            )
            : (
              <FormFields
                fields={currentRelatedFields}
                formData={relatedModalFormData}
                setFormData={setRelatedModalFormData}
                selectOptionsMap={relatedSelectOptions}
                errors={relatedFormErrors}
                clearFieldError={clearRelatedFieldError}
                onPreviewDocument={openBinaryDocument}
              />
            )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeRelatedModal}>Cancel</Button>
          {!currentRelatedReadOnly ? (
            <Button variant="contained" onClick={saveRelatedModal}>Save</Button>
          ) : null}
        </DialogActions>
      </Dialog>
    </Card>
  );
}
